import React from "react";
// UI Framework
import { Checkbox } from "antd";
// Common Components
import {
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  FileDownloader,
  StaticDropdown,
} from "src/Components";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
// Local Components
import AllocationTableUI from "../StatusSummary/AllocationTableUI";
// Requests
import {
  getPortfoGroupInventoryTable,
  getPortfoGroupInventoryExcel,
  getInventoryAssetTypesDropDown,
} from "src/Requests/portfolioGroups";
// Interfaces
import type {
  IPortfoGroupInventorySearchParams,
  IPortfoGroupInventoryTableReport,
} from "src/Interfaces/portfolioGroups";
import { portfolioGroupExchangeTypeOptions } from "src/Utils/exchangeTypeOptions";
import PortfolioFilterMultiSelects from "../PortfolioFilterMultiSelects";
import { getPortfolioGroupNonOptionInstrumentOptions } from "../portfolioGroupInstrumentOptions";

const InventorySummary = () => {
  const [showHiddenCols, setShowHiddenCols] = React.useState(false);
  const [tableRefreshKey, setTableRefreshKey] = React.useState(0);
  const [searchParams, setSearchParams] =
    React.useState<IPortfoGroupInventorySearchParams>({
      portfoGroupIds: [],
      accountIds: [],
      assetTypes: [],
      instrumentId: null,
      exchangeType: null,
    });

  const filterChangeHandler = (name: string, value: number | string) => {
    setSearchParams((prev) => ({
      ...prev,
      [name]:
        value === "" || value == null
          ? null
          : name === "instrumentId"
            ? String(value)
            : Number(value),
    }));
  };

  const multiSelectFilterChangeHandler = (
    name: "portfoGroupIds" | "accountIds",
    value: string[],
  ) => {
    setSearchParams((prev) => ({ ...prev, [name]: value.map(Number) }));
  };

  const assetTypeFilterChangeHandler = (_: string, value: string[]) => {
    setSearchParams((prev) => ({ ...prev, assetTypes: value.map(Number) }));
  };

  const showHiddenColsChangeHandler = (checked: boolean) => {
    setShowHiddenCols(checked);
    setTableRefreshKey((current) => current + 1);
  };

  return (
    <div className="flex flex-col flex-1 w-full gap-3">
      <div className="flex flex-row justify-between items-end gap-3">
        <div className="flex flex-row justify-start items-end gap-3">
          <PortfolioFilterMultiSelects
            portfoGroupIds={searchParams.portfoGroupIds ?? []}
            accountIds={searchParams.accountIds ?? []}
            onChange={multiSelectFilterChangeHandler}
          />
          <div className="w-[220px]">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              options={portfolioGroupExchangeTypeOptions}
              onChange={filterChangeHandler}
              value={searchParams.exchangeType?.toString() ?? ""}
            />
          </div>
          <div className="w-[220px]">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "assetTypes",
                placeholder: "طبقه دارایی",
                extraLabel: true,
              }}
              getData={getInventoryAssetTypesDropDown}
              onChange={assetTypeFilterChangeHandler}
              value={searchParams.assetTypes ?? []}
              withSelectAll
            />
          </div>
          <div className="w-[220px]">
            <DynamicDropdown
              dropdown={{
                name: "instrumentId",
                placeholder: "نماد",
                extraLabel: true,
              }}
              getData={getPortfolioGroupNonOptionInstrumentOptions}
              onChange={filterChangeHandler}
              value={searchParams.instrumentId ?? ""}
            />
          </div>
          <div className="w-36">
            <FileDownloader getFile={() => getPortfoGroupInventoryExcel(searchParams)} />
          </div>
        </div>
      </div>
      <div className="min-h-8 flex items-center mb-2">
        <Checkbox
          checked={showHiddenCols}
          onChange={(event) => showHiddenColsChangeHandler(event.target.checked)}
        >
          نمایش ارزش
        </Checkbox>
      </div>
      <TableTemplate<
        IPortfoGroupInventorySearchParams,
        IPortfoGroupInventoryTableReport
      >
        key={tableRefreshKey}
        getDataTable={getPortfoGroupInventoryTable}
        searchParameters={searchParams}
        TableUI={({ reports, onAction, sortingType, setSortingType }) => (
          <AllocationTableUI
            reports={reports}
            onAction={onAction}
            sortingType={sortingType}
            setSortingType={setSortingType}
            showHiddenCols={showHiddenCols}
            compactPercentAndExpandValue
            autoSizeAccountColumns
            tableBoxClassName="max-h-[800px] portfolio-group-first-row"
          />
        )}
        refreshTableTime={30000}
        pagesize={200}
        withSort
        tableWrapperClassname="!mx-0"
      />
    </div>
  );
};

export default InventorySummary;
